import dayjs from 'dayjs';
import { Formik } from 'formik';
import { useMemo, useState } from 'react';
import { generatePath } from 'react-router-dom';

import { List } from 'components/list';
import { Flex } from 'components/flex';
import { Icon } from 'components/icon';
import { useAppSelector } from 'store';
import { capitalize } from 'utils/string';
import { Avatar } from 'components/avatar';
import { Drawer } from 'components/drawer';
import { Loader } from 'components/loader';
import { Button } from 'components/button';
import { Messages, AppRoutes } from 'config';
import { Checkbox } from 'components/checkbox';
import { ListCard } from 'components/list-card';
import { useListQuery } from 'store/api/storage';
import { ListHeader } from 'components/list-header';
import { useCreateMutation } from 'store/api/refund';
import { isAuthCompanyUser } from 'store/slices/auth';
import { ErrorMessage } from 'components/error-message';
import { useReceiptDetailsQuery } from 'store/api/event';
import { DeleteButton } from 'components/specified-buttons';
import { useVerifyApprovalPasswordMutation } from 'store/api/users';
import { formatPrice, makeSelectOptionsFromEntities } from 'utils/helpers';
import {
  CardType,
  ButtonColors,
  CardTypeIcon,
  RefundPaymentMethod,
} from 'types';
import {
  useAddMutation,
  useDetailsQuery as useCartDetailsQuery,
} from 'store/api/cart';
import {
  useAlert,
  isMobile,
  isDesktop,
  useNavigate,
  handleMutation,
} from 'hooks';
import {
  InputField,
  SwitchField,
  SelectField,
  CheckboxField,
} from 'components/fields';

import * as Styles from './styles';
import { calculateMaxRefundAmounts } from './helpers';
import { FormValues, RefundDrawerProps } from './types';
import { EditPayment, CreatePayment } from './payments-dialog';
import { RefundExchangeDialog } from './refund-exchange-dialog';
import {
  titles,
  template,
  cellsMobile,
  paymentTitles,
  paymentTemplate,
  getInitialValues,
  paymentCellsMobile,
  getValidationSchema,
  getSummaryCardItems,
} from './config';

export const RefundExchangeDrawer = ({
  opened,
  eventId,
  setOpened,
  receiptId,
  isExchange,
  hideManagerPin,
}: RefundDrawerProps) => {
  const mobile = isMobile();
  const desktop = isDesktop();
  const navigate = useNavigate();
  const { showSuccessAlert } = useAlert();
  const [isLoading, setLoading] = useState(false);
  const isCompanyOwner = useAppSelector(isAuthCompanyUser);

  const { data } = useReceiptDetailsQuery(
    { id: +eventId!, receiptId: receiptId! },
    { skip: !eventId || !receiptId }
  );

  const { data: cart } = useCartDetailsQuery(eventId!, {
    skip: !eventId || isCompanyOwner,
  });

  const isClosedEvent = dayjs().isAfter(data?.event?.endDate, 'day');

  const { isFetching, data: storages } = useListQuery(
    { type: 'storage' },
    {
      skip: !isClosedEvent,
    }
  );

  const [add, addCartMutation] = useAddMutation();
  const [createRefund, mutation] = useCreateMutation();
  const [verifyApprovalPassword, verifyMutation] =
    useVerifyApprovalPasswordMutation();

  handleMutation(verifyMutation);
  handleMutation(addCartMutation);
  handleMutation({
    ...mutation,
    onSuccess: () => {
      showSuccessAlert(`Refund ${Messages.CREATED_SUCCESSFULLY}`);
    },
  });

  const {
    maxAllowedTipRefund,
    maxAllowedFreeShippingRefund,
    maxAllowedProcessingFeeRefund,
  } = useMemo(() => calculateMaxRefundAmounts(data), [data]);

  const allowedPaymentMethodTypes = Array.from(
    new Set(data?.paymentMethods?.map((payment) => payment.type))
  );

  const hasRefunds = data?.refund && data?.refund?.length > 0;

  const commonPaymentProps = {
    hasRefunds,
    checkout: data,
    tip: data?.tip,
    maxAllowedTipRefund,
    allowedPaymentMethodTypes,
    maxAllowedFreeShippingRefund,
    maxAllowedProcessingFeeRefund,
  };

  const onSubmitRefund = async ({
    values,
    resetForm,
  }: {
    values: FormValues;
    resetForm: () => void;
  }) => {
    const selectedItems = values?.items?.filter((item) => item.selected);

    const response = await createRefund({
      ...values,
      id: Number(data?.id),
      items: selectedItems,
    }).unwrap();

    if (response) {
      setOpened(false);
      resetForm();
    }
  };

  const onVerifyApprovalPassword = async (approvalPassword: string) => {
    const verifyResponse = await verifyApprovalPassword({
      approvalPassword,
      id: Number(data?.event?.leaderId),
    }).unwrap();

    if (!verifyResponse.isPasswordValid) {
      return;
    }
  };

  const onSubmitExchange = async ({ values }: { values: FormValues }) => {
    if (!hideManagerPin) {
      await onVerifyApprovalPassword(values.approvalPassword || '');
    }

    const selectedItems = values?.items?.filter((item) => item.selected);

    const [response] = await Promise.all(
      selectedItems?.map((item) =>
        add({
          ...item,
          id: +eventId!,
          isExchange: true,
          price: -Math.abs(item.price),
          exchangeId: Number(receiptId),
          exchangeStorageId: values.storageId
            ? Number(values.storageId)
            : undefined,
        }).unwrap()
      )
    );

    if (response.id) {
      showSuccessAlert(
        'The item for exchange has been successfully added to cart'
      );
      navigate(generatePath(AppRoutes.Products.Home, { id: eventId }));
    }
  };

  return (
    <Loader isFullScreen isLoading={isLoading}>
      <Drawer
        opened={opened && !isLoading}
        topOffset={!desktop ? '20%' : undefined}
      >
        <h2>{isExchange ? 'Exchange' : 'Refund'}</h2>

        <Formik<FormValues>
          enableReinitialize
          initialValues={getInitialValues({ data, cart })}
          validationSchema={getValidationSchema({
            data,
            isExchange,
            isClosedEvent,
            hideManagerPin,
            isManualCreditPaymentEnabled: data?.isManualCreditPaymentEnabled,
          })}
          onSubmit={async (values, { resetForm }) => {
            try {
              setLoading(true);

              if (isExchange) {
                await onSubmitExchange({ values });
                return;
              }

              await onSubmitRefund({ values, resetForm });
            } catch (error) {
              console.error(error);
            } finally {
              setLoading(false);
            }
          }}
        >
          {({
            dirty,
            errors,
            values,
            touched,
            resetForm,
            submitForm,
            isSubmitting,
            setFieldValue,
          }) => {
            const acceptDialog = (
              <RefundExchangeDialog
                onConfirm={submitForm}
                isExchange={isExchange}
                hideManagerPin={hideManagerPin}
                disabled={!dirty || isSubmitting}
              />
            );

            return (
              <Styles.Form>
                <Flex column gap={16}>
                  <List
                    count={data?.items?.length}
                    mainWrapperStyles={{ padding: 0 }}
                    topHeader={
                      touched.items && typeof errors?.items === 'string' ? (
                        <ErrorMessage>{errors.items}</ErrorMessage>
                      ) : undefined
                    }
                    header={
                      <ListHeader
                        padding="8px"
                        template={template}
                        titles={[
                          <Checkbox
                            disabled={values?.items?.every(
                              (item) => item.availableQuantity === 0
                            )}
                            checked={values?.items?.every((item) => {
                              if (item.availableQuantity > 0) {
                                return item.selected;
                              }

                              return true;
                            })}
                            onChange={(e) => {
                              data?.items?.forEach(async (_, index) => {
                                const hasAvailableQuantity =
                                  values?.items?.[index]?.availableQuantity > 0;

                                if (hasAvailableQuantity) {
                                  await setFieldValue(
                                    `items.${index}.selected`,
                                    e.target.checked
                                  );
                                }
                              });
                            }}
                          />,
                          ...titles,
                        ]}
                      />
                    }
                  >
                    {values?.items.map((item, index) => {
                      const isEmptyAvailableQuantity =
                        item.availableQuantity === 0;

                      return (
                        <ListCard
                          key={item.id}
                          template={template}
                          tabletTemplate={template}
                          cellsMobile={cellsMobile}
                          headers={
                            mobile ? ['Select', ...titles] : ['', ...titles]
                          }
                          values={[
                            <CheckboxField
                              name={`items.${index}.selected`}
                              disabled={isEmptyAvailableQuantity}
                              label={mobile ? 'Select this product' : undefined}
                            />,
                            <Flex gap={8} alignItems="center">
                              <Avatar url={item.product.image} />
                              <b>{item.product.name}</b>
                            </Flex>,
                            <InputField
                              number
                              placeholder="Item Quantity"
                              max={item.availableQuantity}
                              name={`items.${index}.quantity`}
                              disabled={isEmptyAvailableQuantity}
                            />,
                            <InputField
                              number
                              decimal
                              placeholder="Item Price"
                              max={Number(item.sellPrice)}
                              name={`items.${index}.price`}
                              disabled={isEmptyAvailableQuantity}
                            />,
                            formatPrice(item.price),
                            <SwitchField
                              name={`items.${index}.isDamaged`}
                              disabled={isEmptyAvailableQuantity}
                            />,
                          ]}
                        />
                      );
                    })}
                  </List>

                  {isClosedEvent && (
                    <Flex>
                      <SelectField
                        onlyValue
                        name="storageId"
                        isDisabled={isFetching}
                        label="Select Return Storage"
                        placeholder="Select Return Storage"
                        options={makeSelectOptionsFromEntities(storages)}
                      />
                    </Flex>
                  )}
                </Flex>

                <Flex
                  gap={16}
                  flexDirection={!desktop ? 'column-reverse' : 'row'}
                >
                  <Styles.SummaryCard
                    items={getSummaryCardItems({
                      data,
                      values,
                      isExchange,
                      maxAllowedTipRefund,
                      maxAllowedFreeShippingRefund,
                      maxAllowedProcessingFeeRefund,
                    })}
                  />
                  <Flex fullWidth flexDirection="column">
                    {touched.returnPayments &&
                      typeof errors?.returnPayments === 'string' && (
                        <ErrorMessage>{errors.returnPayments}</ErrorMessage>
                      )}
                    {!!values?.returnPayments?.length && (
                      <List
                        mainWrapperStyles={{ padding: 0 }}
                        count={values?.returnPayments?.length}
                        header={
                          <ListHeader
                            padding="8px"
                            titles={paymentTitles}
                            template={paymentTemplate}
                          />
                        }
                      >
                        {values?.returnPayments?.map((payment) => {
                          const isCashType =
                            payment?.type === RefundPaymentMethod.Cash;

                          const cloverPayment = data?.cloverPayments?.find(
                            (clover) =>
                              clover.paymentId === payment.cloverPaymentId
                          );

                          const creditCardIcon = cloverPayment?.type ? (
                            CardTypeIcon[cloverPayment.cardType as CardType]
                          ) : (
                            <Icon.Credit width={24} height={24} />
                          );

                          return (
                            <ListCard
                              nativeIconColor
                              key={payment.id}
                              headers={paymentTitles}
                              template={paymentTemplate}
                              tabletTemplate={paymentTemplate}
                              cellsMobile={paymentCellsMobile}
                              values={[
                                <Flex gap={8} alignItems="center">
                                  {isCashType ? (
                                    <Icon.Cash width={24} height={24} />
                                  ) : (
                                    creditCardIcon
                                  )}

                                  <Flex column>
                                    <p>Return to {capitalize(payment.type)}</p>
                                    {!isCashType && cloverPayment && (
                                      <Styles.Subtitle>
                                        Credit card ending in{' '}
                                        {cloverPayment?.last4}
                                      </Styles.Subtitle>
                                    )}
                                  </Flex>
                                </Flex>,
                                formatPrice(payment.amount),
                                payment.tip && formatPrice(payment.tip),
                                payment.processingFee &&
                                  formatPrice(payment.processingFee),
                                payment.freeShipping &&
                                  formatPrice(payment.freeShipping),
                                <Flex gap={16}>
                                  <EditPayment
                                    {...commonPaymentProps}
                                    data={payment}
                                  />
                                  <DeleteButton
                                    fullWidth={mobile}
                                    onClick={() =>
                                      setFieldValue(
                                        'returnPayments',
                                        values.returnPayments.filter(
                                          (item) => item.id !== payment.id
                                        )
                                      )
                                    }
                                  />
                                </Flex>,
                              ]}
                            />
                          );
                        })}
                      </List>
                    )}
                  </Flex>
                </Flex>

                <Styles.Buttons>
                  <Button
                    text="Cancel"
                    colorType={ButtonColors.LightRed}
                    onClick={() => {
                      resetForm();
                      setOpened(false);
                    }}
                  />

                  <Flex gap={8} column={mobile}>
                    {isExchange ? (
                      acceptDialog
                    ) : (
                      <>
                        <CreatePayment {...commonPaymentProps} />
                        {acceptDialog}
                      </>
                    )}
                  </Flex>
                </Styles.Buttons>
              </Styles.Form>
            );
          }}
        </Formik>
      </Drawer>
    </Loader>
  );
};
